export default class PrintableHtml {

    static styles = {
        sLine: {
            color: 'black',
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            lineHeight: '1.2',
            minHeight: '10px',
            breakInside: 'avoid',
            whiteSpace: 'pre-wrap',
        },
        sAlignLeft: {
            justifyContent: 'flex-start',
            textAlign: 'left'
        },
        sAlignCenter: {
            justifyContent: 'center',
            textAlign: 'center'
        },
        sAlignRight: {
            justifyContent: 'flex-end',
            textAlign: 'right'
        }
    }

    constructor(printableContent, wrapperClass = null) {
        this.printableContent = printableContent;
        this.wrapperClass = wrapperClass;
    }

    /**
     * Processes the printable content and generates formatted html.
     *
     * The method processes the printable content based on its type ('S', 'B', 'D', 'L', 'T')
     * and calls the corresponding process function.
     *
     * @returns {string[]} An array of formatted html lines. Each line is a string and corresponds to a printable entity.
     */
    process() {
        const html = this.printableContent.flatMap(c => {
            if(c.type === 'L') {
                const content = c.data[0].content[0] || ''
                const align = c.data[0].align || 'L'
                return this.processLine(content, align)
            } else if(c.type === 'S') {
                return this.processSpaceBetween(c.data[0].content)
            } else if(c.type === 'B') {
                return this.processSpaceOrBreak(c.data[0].content)
            } else if(c.type === 'D') {
                const delimiter = c.data[0].delimiter || ' '
                const content = c.data[0].content[0] || ''
                const align = c.data[0].align || 'L'
                return this.processDivider(delimiter, content, align)
            } else if(c.type === 'T') {
                const content = c.data || []
                const colNumber = c.colNumber || 2
                const colWidths = c.html?.colWidths || []
                const style = c.html?.style || null
                return this.processTable(content, colNumber, colWidths, style)
            }
        })

        return [
            !this.wrapperClass ? '<div>' : `<div class="${this.wrapperClass}">`,
            ...html,
            '</div>'
        ]
    }

    /**
     * Process the input data and create a formatted table as string.
     *
     * @param {Array} data - The data to be inserted into the table. It should be an array of objects, each containing column data and options.
     * @param {Number} colNumber - The number of columns in the table.
     * @param {Array} [colWidths=[]] - An optional array specifying the width of each column. If not provided, column widths will be calculated automatically.
     * @param {object|null} tableStyle - Optional. The style to apply to the table. If not provided or set to null, no style will be applied.
     * @return {String[]} Returns an array of string where each string is a row in the table.
     */
    processTable(data, colNumber, colWidths = [], tableStyle = null) {
        const style = this.createStyleString({
            borderCollapse: 'collapse',
            flex: '1',
            ...tableStyle,
        })

        const html = [`<div style="display: flex">`]
        html.push(`<table style="${style}">`)

        html.push('<colgroup>')
        for(let i = 0; i < colNumber; i++) {
            if(colWidths[i]) html.push(`<col style="width: ${colWidths[i]}">`)
            else html.push(`<col>`)
        }
        html.push('</colgroup>')

        const cols = JSON.parse(JSON.stringify(data)).map(col => {
            col.content = col.content.join('<br>')

            if(!col.style) col.style = {}

            col.style.textAlign = 'left'
            if(col.align === 'C') col.style.textAlign = 'center'
            else if(col.align === 'R') col.style.textAlign = 'right'
            delete col.align

            return col
        })

        const rows = []
        let curRow = []
        const dividers = []
        cols.forEach((col, index) => {
            if(col.content === 'divider') {
                if(index === 0) return

                rows.push([...curRow])
                dividers.push(rows.length)
                curRow = []
                return
            }

            const cellsUsed = curRow.reduce((a, b) => a + (b.colSpan || 1), 0);
            if((cellsUsed + (col.colSpan || 1) <= colNumber)) {
                curRow.push(col)
            } else {
                rows.push([...curRow])
                curRow = [col]
            }

            if(index === cols.length - 1) {
                rows.push([...curRow])
            }
        })

        rows.forEach((row, index) => {
            if(dividers.includes(index)) {
                const sDivider = this.createStyleString({
                    marginBottom: '9px',
                    paddingTop: '9px',
                    borderBottom: '1px solid black'
                })

                html.push(
                    '<tr>' +
                        `<td colspan="${colNumber}">` +
                            `<div style="${sDivider}"></div>` +
                        '</td>' +
                    `</td>`
                )
            }

            html.push('<tr>')

            row.forEach(col => {
                const style = {
                    whiteSpace: 'pre-wrap',
                    lineHeight: '1.2',
                    minHeight: '10px',
                }

                const colStyle = this.createStyleString({
                    ...style,
                    ...col.style
                });

                html.push(`<td style="${colStyle}" colspan="${col.colSpan || 1}">${col.content}</td>`)
            })

            html.push('</tr>')
        })

        html.push('</table>')
        html.push('</div>')
        return html;
    }

    /**
     * Formats the line according to the alignment
     *
     * @param {string} content - Content of the line
     * @returns {string[]} An array of lines
     */
    processLine(content, align = 'L') {
        const sLine = this.createStyleString(PrintableHtml.styles.sLine);
        const sAlignLeft = this.createStyleString(PrintableHtml.styles.sAlignLeft);
        const sAlignCenter = this.createStyleString(PrintableHtml.styles.sAlignCenter);
        const sAlignRight = this.createStyleString(PrintableHtml.styles.sAlignRight);
        const sHighlight = '';

        let sAlign = sAlignLeft
        if(align === 'C') sAlign = sAlignCenter;
        else if(align === 'R') sAlign = sAlignRight;

        return [`<div style="${sLine} ${sAlign} ${sHighlight}">${content}</div>`]
    }

    /**
     * Formats an array of two strings according to 'space-between' CSS rule
     *
     * @param {string[]} arr - Array of two strings
     * @returns {string[]} An array of lines
     */
    processSpaceBetween(arr) {
        const sLine = this.createStyleString(PrintableHtml.styles.sLine);
        const sHighlight = '';

        return [
            `<div style="${sLine} ${sHighlight}">` +
                `<span style="flex: 1">${arr[0]}</span>` +
                `<span>${arr[1]}</span>` +
            `</div>`
        ]
    }

    /**
     * Formats an array of two strings according to 'space-or-break' rule
     *
     * @param {string[]} arr - Array of two strings
     * @returns {string[]} An array of lines
     */
    processSpaceOrBreak(arr) {
        const sLine = this.createStyleString(PrintableHtml.styles.sLine);
        const sHighlight = this.createStyleString()

        return [`<div style="${sLine} ${sHighlight}">${arr[0]} ${arr[1]}</div>`]
    }

    /**
     * Creates a divider line with text content
     *
     * @param {string} delimiter - The character used as a divider
     * @param {string} content - The text content
     * @param {string} align - The alignment of the text content ('L', 'R', or 'C')
     * @returns {string[]} An array of lines
     */
    processDivider(delimiter, content, align) {
        const sContainer = this.createStyleString({
            color: 'black',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            lineHeight: '1.2',
            minHeight: '18px'
        })
        const sDividerLines = this.createStyleString({
            width: '100%'
        })
        const sDividerLineSolid = this.createStyleString({
            width: '100%',
            borderBottom: '1px solid',
            margin: '2px 0'
        })
        const sDividerLineDashed = this.createStyleString({
            width: '100%',
            borderBottom: '1.5px dashed',
            margin: '2px 0'
        })
        const sDividerLineUnderscore = this.createStyleString({
            width: '100%',
            borderBottom: '1px solid black',
            margin: '2px 0',
            alignSelf: 'flex-end'
        })
        const sDividerText = this.createStyleString({
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            background: 'white',
            flex: 'none'
        })
        const sPaddingLeft = this.createStyleString({
            paddingLeft: '13px'
        })
        const sPaddingRight = this.createStyleString({
            paddingRight: '13px'
        })
        const sHighlight = this.createStyleString()

        const html = [`<div style="${sContainer}">`]

        let dividerLineSolid =
            `<div style="${sDividerLines}">` +
                `<div style="${sDividerLineSolid}"></div>` +
            `</div>`
        let dividerLineDoubleSolid =
            `<div style="${sDividerLines}">` +
                `<div style="${sDividerLineSolid}"></div>` +
                `<div style="${sDividerLineSolid}"></div>` +
            `</div>`
        let dividerLineDashed =
            `<div style="${sDividerLines}">` +
                `<div style="${sDividerLineDashed}"></div>` +
            `</div>`
        let dividerLineDoubleDashed =
            `<div style="${sDividerLines}">` +
                `<div style="${sDividerLineDashed}"></div>` +
                `<div style="${sDividerLineDashed}"></div>` +
            `</div>`
        let dividerLineUnderscore = `<div style="${sDividerLineUnderscore}"></div>`

        let dividerLines = null
        if(delimiter === '-') dividerLines = dividerLineSolid
        else if(delimiter === '=') dividerLines = dividerLineDoubleSolid
        else if(delimiter === '--') dividerLines = dividerLineDashed
        else if(delimiter === '==') dividerLines = dividerLineDoubleDashed
        else if(delimiter === '_') dividerLines = dividerLineUnderscore
        else return []

        if(align !== 'L') {
            html.push(dividerLines)
        }

        if(content) {
            let padding = align !== 'L' ? sPaddingLeft : '';
            padding += align !== 'R' ? sPaddingRight : '';
            html.push(`<div style="${sDividerText} ${sHighlight} ${padding}">${content}</div>`)
        }

        if(align !== 'R') {
            html.push(dividerLines)
        }

        html.push(`</div>`)

        return html
    }

    createStyleString(style) {
        if(!style) return '';

        // Convert camelCase to kebab-case
        const styleString = Object.keys(style).map(key => {
            let kebabCaseKey = key.replace(/([a-z0-9]|(?=[A-Z]))([A-Z])/g, '$1-$2').toLowerCase();
            return `${kebabCaseKey}: ${style[key]}`;
        }).join('; ')

        if(!styleString) return '';

        return styleString + ';'
    }
}
